import { useCallback, useEffect, useRef, useState } from 'react';
import type { FocusEvent, ReactNode } from 'react';
import { subscribeToastDismiss, subscribeToasts, toast } from '../lib/toastBus';
import type { ToastMessage } from '../lib/toastBus';

/**
 * Åbne elementer, der selv tager Esc: dialoger (også ikke-modale vinduer), menuer, forslagslister og åbne
 * pop op-knapper. Mens et af dem findes, lukker Esc ingen toast (medmindre fokus står i toasten).
 */
const ESCAPE_OWNERS = [
  '.modal-backdrop',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[role="combobox"][aria-expanded="true"]',
  '[aria-haspopup][aria-expanded="true"]',
].join(', ');

/** Et felt, der selv bruger Esc (fx fortryd en indtastning), markeres med `data-own-escape`. */
const OWN_ESCAPE = '[data-own-escape]';

/** En toasts nedtælling; den står stille, så længe der er et hold (mus over eller fokus i toasten). */
interface Countdown {
  handle: ReturnType<typeof setTimeout> | null;
  remaining: number;
  startedAt: number;
  holds: Set<'hover' | 'focus'>;
}

/**
 * Viser toasts nederst til højre (fra 0.32.0; UX.md afsnit 4). En toast forsvinder ikke, mens musen er over den,
 * eller den har fokus (WCAG 2.2.1), og Esc lukker toasten med fokus, ellers den nyeste (UX.md afsnit 5). Lukkes en
 * toast med fokus, går fokus tilbage til elementet, der havde det før.
 */
export function ToastHost() {
  const [items, setItems] = useState<ToastMessage[]>([]);
  const itemsRef = useRef<ToastMessage[]>([]);
  const timers = useRef(new Map<string, Countdown>());
  const containerRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
    timers.current.delete(id);
    const element = containerRef.current?.querySelector(`[data-toast-id="${id}"]`);
    if (element && element.contains(document.activeElement)) {
      const back = returnFocus.current;
      returnFocus.current = null;
      if (back?.isConnected) back.focus({ preventScroll: true });
    }
    itemsRef.current = itemsRef.current.filter((item) => item.id !== id);
    setItems(itemsRef.current);
  }, []);

  const start = useCallback(
    (id: string) => {
      const timer = timers.current.get(id);
      if (!timer || timer.handle || timer.holds.size > 0) return;
      timer.startedAt = Date.now();
      timer.handle = setTimeout(() => dismiss(id), Math.max(0, timer.remaining));
    },
    [dismiss],
  );

  const hold = useCallback((id: string, reason: 'hover' | 'focus') => {
    const timer = timers.current.get(id);
    if (!timer) return;
    timer.holds.add(reason);
    if (timer.handle) {
      clearTimeout(timer.handle);
      timer.handle = null;
      timer.remaining -= Date.now() - timer.startedAt;
    }
  }, []);

  const release = useCallback(
    (id: string, reason: 'hover' | 'focus') => {
      const timer = timers.current.get(id);
      if (!timer) return;
      timer.holds.delete(reason);
      start(id);
    },
    [start],
  );

  useEffect(() => {
    const active = timers.current;
    const unsubscribe = subscribeToasts((message) => {
      itemsRef.current = [...itemsRef.current, message];
      setItems(itemsRef.current);
      if (message.duration > 0) {
        active.set(message.id, { handle: null, remaining: message.duration, startedAt: 0, holds: new Set() });
        start(message.id);
      }
    });
    const unsubscribeDismiss = subscribeToastDismiss(dismiss);
    return () => {
      unsubscribe();
      unsubscribeDismiss();
      for (const timer of active.values()) if (timer.handle) clearTimeout(timer.handle);
      active.clear();
    };
  }, [dismiss, start]);

  // Esc lukker toasten med fokus (og intet andet), ellers den nyeste – men aldrig, mens en dialog, menu,
  // forslagsliste eller et felt med egen Esc tager tasten. Lytteren ligger i capture-fasen på window, så den kører
  // før alle andre Esc-lyttere og ser DOM'en, som den var, da tasten blev trykket.
  const hasItems = items.length > 0;
  useEffect(() => {
    if (!hasItems) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const focused = document.activeElement?.closest<HTMLElement>('[data-toast-id]');
      if (focused) {
        event.preventDefault();
        event.stopPropagation();
        if (focused.dataset.toastId) dismiss(focused.dataset.toastId);
        return;
      }
      if (document.querySelector(ESCAPE_OWNERS)) return;
      if (event.target instanceof Element && event.target.closest(OWN_ESCAPE)) return;
      const id = itemsRef.current[itemsRef.current.length - 1]?.id;
      if (id) dismiss(id);
    }
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [hasItems, dismiss]);

  /**
   * Fokus kommer udefra: elementet huskes, så fokus kan gå tilbage dertil, når toasten lukkes. Kommer fokus fra
   * ingenting (fx et museklik), huskes intet.
   */
  function onContainerFocus(event: FocusEvent<HTMLDivElement>) {
    const from = event.relatedTarget;
    if (from instanceof Node && event.currentTarget.contains(from)) return;
    returnFocus.current = from instanceof HTMLElement ? from : null;
  }

  /** Fokus forlader toasterne: det huskede element glemmes, så en senere toast ikke sender fokus dertil. */
  function onContainerBlur(event: FocusEvent<HTMLDivElement>) {
    const to = event.relatedTarget;
    if (!(to instanceof Node && event.currentTarget.contains(to))) returnFocus.current = null;
  }

  if (items.length === 0) return null;

  return (
    <div className="toast-container" ref={containerRef} onFocus={onContainerFocus} onBlur={onContainerBlur}>
      {items.map((item) => (
        // En fejl (gem blokeret af validering) meldes med role="alert"
        // (assertive), resten med role="status" (polite) – brief 5.
        <div
          key={item.id}
          data-toast-id={item.id}
          className={`toast ${item.type}`}
          role={item.type === 'error' ? 'alert' : 'status'}
          aria-live={item.type === 'error' ? 'assertive' : 'polite'}
          onMouseEnter={() => hold(item.id, 'hover')}
          onMouseLeave={() => release(item.id, 'hover')}
          onFocus={() => hold(item.id, 'focus')}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) release(item.id, 'focus');
          }}
        >
          <span className="toast-message">{item.message}</span>
          {item.action && (
            <button
              type="button"
              className="link-button"
              onClick={() => {
                item.action?.onClick();
                dismiss(item.id);
              }}
            >
              {item.action.label}
            </button>
          )}
          <button
            type="button"
            className="toast-dismiss"
            aria-label="Luk besked"
            onClick={() => dismiss(item.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/** Bekvem hook, saa komponenter ikke behoever at importere bussen direkte. */
export function useToast() {
  return toast;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <ToastHost />
    </>
  );
}
